import axios, { type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "../store/authStore";
import { clearClientSession } from "../store/session";

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<string | null> | null = null;

export const api = axios.create({
  baseURL: "/api/v1",
  timeout: 30_000,
});

function isAuthEndpoint(url?: string) {
  return !!url && (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh")
  );
}

function redirectToLogin() {
  clearClientSession();
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign("/login");
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        "/api/v1/auth/refresh",
        { refresh_token: refreshToken },
        { timeout: 10_000 },
      )
      .then((r) => {
        const accessToken = r.data?.access_token;
        const nextRefreshToken = r.data?.refresh_token;
        if (!accessToken || !nextRefreshToken) return null;
        useAuthStore.getState().set({
          accessToken,
          refreshToken: nextRefreshToken,
        });
        return accessToken as string;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config as RetryableConfig | undefined;

    if (status === 401 && original && !original._retry && !isAuthEndpoint(original.url)) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      redirectToLogin();
    } else if (status === 401 && !isAuthEndpoint(original?.url)) {
      redirectToLogin();
    }

    return Promise.reject(err);
  },
);
